/**
 * Board pool — manages loading and random selection of puzzle boards
 * from the bundled JSON files. Handles the 50/30/20 difficulty distribution.
 */

import type { GameConfig, GridSize } from '@/types/sliding-puzzle';

import gameConfig from '../data/game-config.json';

// ── Types ────────────────────────────────────────────────────────────────────

/** Shape of each board JSON file in the data/boards directory. */
export interface BoardJson {
  levelId: string;
  name: string;
  gridSize: GridSize;
  fileUrl: string;
  shuffles: number[][];
}

/** A board selected for a game session, with a specific shuffle chosen. */
export interface SelectedBoard {
  name: string;
  gridSize: GridSize;
  fileUrl: string;
  pieces: number[];
  levelName: string;
  displayTime: number;
  maxScore: number;
}

// ── Load all board JSONs via Vite glob import ────────────────────────────────

type BoardModule = { default: BoardJson };

const easyModules = import.meta.glob<BoardModule>(
  '../data/boards/easy/*.json',
  { eager: true },
);
const mediumModules = import.meta.glob<BoardModule>(
  '../data/boards/medium/*.json',
  { eager: true },
);
const hardModules = import.meta.glob<BoardModule>(
  '../data/boards/hard/*.json',
  { eager: true },
);
const demoModules = import.meta.glob<BoardModule>(
  '../data/boards/demo/*.json',
  { eager: true },
);

function extractBoards(modules: Record<string, BoardModule>): BoardJson[] {
  return Object.values(modules).map((mod) => mod.default);
}

const easyBoards = extractBoards(easyModules);
const mediumBoards = extractBoards(mediumModules);
const hardBoards = extractBoards(hardModules);
const demoBoards = extractBoards(demoModules);

// ── Helpers ──────────────────────────────────────────────────────────────────

/** Fisher-Yates shuffle — returns a new shuffled copy. */
function shuffleArray<T>(arr: T[]): T[] {
  const copy = [...arr];
  for (let i = copy.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [copy[i], copy[j]] = [copy[j], copy[i]];
  }
  return copy;
}

/** Pick `count` random unique items from an array. */
function pickRandom<T>(arr: T[], count: number): T[] {
  const shuffled = shuffleArray(arr);
  return shuffled.slice(0, Math.min(count, shuffled.length));
}

/** Pick one random shuffle from a board's shuffles array. */
function pickRandomShuffle(board: BoardJson): number[] {
  const idx = Math.floor(Math.random() * board.shuffles.length);
  return [...board.shuffles[idx]];
}

// ── Config access ────────────────────────────────────────────────────────────

export function getGameConfig() {
  return gameConfig;
}

// ── Board selection ──────────────────────────────────────────────────────────

export type BoardDifficulty = 'easy' | 'medium' | 'hard';

/**
 * Select boards for a game session based on the configured counts.
 *
 * If a difficulty is specified, it returns only boards of that difficulty.
 * Otherwise, it returns boards in play order: Easy, then Medium, then Hard.
 *
 * Boards within each difficulty are randomly selected without replacement.
 * A random shuffle is chosen from each selected board.
 */
export function selectSessionBoards(difficulty?: BoardDifficulty, customBoardCount?: number): SelectedBoard[] {
  const config = gameConfig;

  const easyConfig = config.levels.find((l) => l.name === 'Easy');
  const mediumConfig = config.levels.find((l) => l.name === 'Medium');
  const hardConfig = config.levels.find((l) => l.name === 'Hard');

  const easyCount = customBoardCount ?? easyConfig?.boardCount ?? 5;
  const mediumCount = customBoardCount ?? mediumConfig?.boardCount ?? 5;
  const hardCount = customBoardCount ?? hardConfig?.boardCount ?? 5;

  const levelMap: Record<
    BoardDifficulty,
    { boards: BoardJson[]; count: number; levelName: string; displayTime: number; maxScore: number }
  > = {
    easy: {
      boards: easyBoards,
      count: easyCount,
      levelName: 'Easy',
      displayTime: easyConfig?.displayTime ?? 3,
      maxScore: easyConfig?.maxScore ?? 100,
    },
    medium: {
      boards: mediumBoards,
      count: mediumCount,
      levelName: 'Medium',
      displayTime: mediumConfig?.displayTime ?? 3,
      maxScore: mediumConfig?.maxScore ?? 100,
    },
    hard: {
      boards: hardBoards,
      count: hardCount,
      levelName: 'Hard',
      displayTime: hardConfig?.displayTime ?? 3,
      maxScore: hardConfig?.maxScore ?? 150,
    },
  };

  const selected: SelectedBoard[] = [];
  
  let requested: BoardDifficulty[] = ['easy', 'medium', 'hard'];
  if (difficulty === 'easy') {
    requested = ['easy', 'medium', 'hard'];
  } else if (difficulty === 'medium') {
    requested = ['medium', 'hard'];
  } else if (difficulty === 'hard') {
    requested = ['hard'];
  }

  for (const d of requested) {
    const { boards, count, levelName, displayTime, maxScore } = levelMap[d];
    const picked = pickRandom(boards, count);
    for (const board of picked) {
      selected.push({
        name: board.name,
        gridSize: board.gridSize,
        fileUrl: board.fileUrl,
        pieces: pickRandomShuffle(board),
        levelName,
        displayTime,
        maxScore,
      });
    }
  }

  return selected;
}

/**
 * Select boards for the demo flow.
 * Picks `boardCount` random boards from the demo pool (per config).
 */
export function selectDemoBoards(): SelectedBoard[] {
  const config = gameConfig;
  const demoCfg = config.demoLevels[0];

  const count = demoCfg.boardCount;
  const picked = pickRandom(demoBoards, count);

  return picked.map((board) => ({
    name: board.name,
    gridSize: board.gridSize,
    fileUrl: board.fileUrl,
    pieces: pickRandomShuffle(board),
    levelName: 'Demo',
    displayTime: demoCfg.displayTime,
    maxScore: 0,
  }));
}

/**
 * Provide a typed GameConfig from the board pool data for external consumers.
 * Maps each selected board to the GameConfig type.
 */
export function toGameConfig(board: SelectedBoard): GameConfig {
  return {
    levelId: '',
    name: board.name,
    gridSize: board.gridSize,
    fileUrl: board.fileUrl,
    shuffles: [board.pieces],
  };
}
